<template>
  <div class="playground-page">
    <nav-bar></nav-bar>

    <v-container fluid class="playground-container">
      <!-- ============================ כותרת ============================ -->
      <v-card elevation="3" class="hero-card mb-4">
        <div class="hero-strip"></div>
        <v-card-text class="pa-6">
          <div class="d-flex align-center flex-wrap">
            <v-avatar :color="navy" size="52" class="ml-4">
              <v-icon color="white" size="28">mdi-api</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <h1 class="text-h4 font-weight-bold mb-1" :style="{ color: navy }">סביבת בדיקות API</h1>
              <p class="mb-0 grey--text text--darken-1">
                בחרו פעולה, מלאו את השדות ושלחו בקשה אמיתית לשרת. התשובה, כותרות התגובה והקוד המוכן להעתקה מופיעים כאן
                למטה.
              </p>
            </div>
            <v-btn text :color="navy" @click="showLegend = !showLegend" class="mt-2 mt-sm-0">
              <v-icon left>mdi-information-outline</v-icon>
              קודי סטטוס
            </v-btn>
          </div>

          <v-expand-transition>
            <div v-if="showLegend" class="legend-box mt-4">
              <div class="d-flex flex-wrap">
                <div v-for="item in statusLegend" :key="item.code" class="legend-item">
                  <v-chip small label :color="item.color" dark class="ml-2">status {{ item.code }}</v-chip>
                  <span class="grey--text text--darken-2">{{ item.text }}</span>
                </div>
              </div>
              <v-divider class="my-3"></v-divider>
              <div class="caption grey--text text--darken-1">
                שדה <code>status</code> בגוף התשובה הוא הסטטוס הלוגי של הפעולה, והוא נפרד מקוד ה-HTTP.
                למשל "לא נמצאו נתונים" מוחזר עם קוד 200 ועם status 4.
              </div>
            </div>
          </v-expand-transition>
        </v-card-text>
      </v-card>

      <!-- ======================= פרטי התחברות ======================= -->
      <v-card elevation="3" class="conn-card mb-4">
        <v-card-title class="pb-2">
          <v-icon :color="navy" class="ml-2">mdi-key-chain-variant</v-icon>
          <span class="text-subtitle-1 font-weight-bold" :style="{ color: navy }">פרטי התחברות</span>
          <v-spacer></v-spacer>
          <v-chip small :color="token ? 'green darken-3' : 'red darken-2'" dark label>
            <v-icon x-small left>{{ token ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
            {{ token ? 'טוקן הוזן' : 'חסר טוקן' }}
          </v-chip>
        </v-card-title>

        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="7" md="5">
              <v-text-field v-model="token" label="Bearer Token (חובה)" outlined dense hide-details="auto" dir="ltr"
                type="text" name="pg-access-key" autocomplete="off" spellcheck="false" data-lpignore="true"
                data-form-type="other" :class="{ 'masked-field': !showToken }" prepend-inner-icon="mdi-key-variant"
                :color="navy" :append-icon="showToken ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="showToken = !showToken">
                <template v-slot:append-outer>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-on="on" v-bind="attrs" :loading="tokenLoading" @click="loadMyToken(false)">
                        <v-icon :color="navy">mdi-account-arrow-left</v-icon>
                      </v-btn>
                    </template>
                    <span>טעינת הטוקן מהחשבון שלי</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="5" md="4">
              <v-text-field v-model="workspaceId" label="Workspace ID (לא חובה)" outlined dense hide-details="auto"
                dir="ltr" name="pg-ws-ref" autocomplete="off" spellcheck="false" data-lpignore="true"
                data-form-type="other" prepend-inner-icon="mdi-account-group-outline" :color="navy"
                hint="נדרש רק למשתמש משני" persistent-hint></v-text-field>
            </v-col>
          </v-row>

          <div class="d-flex align-center flex-wrap conn-footer mt-3">
            <v-chip x-small label outlined class="ml-2">
              <v-icon x-small left>mdi-server</v-icon>
              <span dir="ltr">{{ baseUrl }}</span>
            </v-chip>
            <span class="caption grey--text">
              הנתיבים מוגדרים מראש. שם הטבלה מוזן בשדה שלו בתוך הפעולה, ולידו כפתור לטעינת השדות.
            </span>
          </div>
        </v-card-text>
      </v-card>

      <v-row>
        <!-- ======================= רשימת הפעולות ======================= -->
        <v-col cols="12" md="4" lg="3">
          <v-card elevation="3" class="sidebar-card">
            <v-card-text class="pb-1">
              <v-text-field v-model="search" label="חיפוש פעולה" outlined dense hide-details clearable
                prepend-inner-icon="mdi-magnify" :color="navy"></v-text-field>
            </v-card-text>

            <v-divider></v-divider>

            <div class="endpoint-list">
              <div v-for="group in filteredTree" :key="group.id" class="endpoint-group">
                <div class="group-header">
                  <v-icon small :color="navy" class="ml-2">{{ group.icon }}</v-icon>
                  <span>{{ group.title }}</span>
                  <v-spacer></v-spacer>
                  <span class="group-count">{{ group.count }}</span>
                </div>

                <div v-for="section in group.sections" :key="group.id + '-' + section.id">
                  <div v-if="section.title" class="section-header">{{ section.title }}</div>
                  <div v-for="ep in section.endpoints" :key="ep.id" class="endpoint-item"
                    :class="{ active: selectedId === ep.id }" @click="selectEndpoint(ep.id)">
                    <span class="method-tag" :style="{ backgroundColor: methodColor(ep.method) }">
                      {{ ep.method }}
                    </span>
                    <span class="endpoint-title">{{ ep.title }}</span>
                  </div>
                </div>
              </div>

              <div v-if="!filteredTree.length" class="pa-6 text-center grey--text">
                <v-icon color="grey lighten-1" size="36">mdi-magnify-close</v-icon>
                <div class="mt-2 caption">אין פעולה שתואמת לחיפוש</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- ======================= בונה הבקשה ======================= -->
        <v-col cols="12" md="8" lg="9">
          <template v-if="endpoint">
            <!-- כותרת הפעולה -->
            <v-card elevation="3" class="mb-4 endpoint-card">
              <v-card-text class="pa-5">
                <div class="d-flex align-start flex-wrap">
                  <div class="flex-grow-1">
                    <h2 class="text-h5 font-weight-bold mb-1" :style="{ color: navy }">{{ endpoint.title }}</h2>
                    <p class="mb-2 grey--text text--darken-2">{{ endpoint.description }}</p>
                  </div>
                  <div>
                    <v-chip v-if="endpoint.workspaceIn" small label outlined class="ml-2">
                      <v-icon x-small left>mdi-account-group-outline</v-icon>
                      workspaceId {{ endpoint.workspaceIn === 'query' ? 'ב-URL' : 'בגוף הבקשה' }}
                    </v-chip>
                    <v-chip v-if="endpoint.permission" small label outlined color="orange darken-3" class="ml-2">
                      <v-icon x-small left>mdi-shield-key-outline</v-icon>
                      דורש הרשאת {{ permissionLabel(endpoint.permission) }}
                    </v-chip>
                    <v-chip v-if="endpoint.danger" small label color="red darken-2" dark>
                      <v-icon x-small left>mdi-alert-outline</v-icon>
                      פעולה בלתי הפיכה
                    </v-chip>
                  </div>
                </div>

                <div class="url-bar mt-3">
                  <span class="method-tag lg" :style="{ backgroundColor: methodColor(endpoint.method) }">
                    {{ endpoint.method }}
                  </span>
                  <span class="url-text" dir="ltr">{{ previewUrl }}</span>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-on="on" v-bind="attrs" @click="copyText(previewUrl, 'הכתובת הועתקה')">
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-btn>
                    </template>
                    <span>העתקת הכתובת</span>
                  </v-tooltip>
                </div>

                <div v-if="endpoint.notes && endpoint.notes.length" class="mt-3">
                  <div v-for="(note, i) in endpoint.notes" :key="i" class="note-line">
                    <v-icon x-small color="amber darken-3" class="ml-1">mdi-lightbulb-on-outline</v-icon>
                    <span>{{ note }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- פרמטרים -->
            <v-card elevation="3" class="mb-4" :key="endpoint.id">
              <v-card-title class="py-3">
                <v-icon :color="navy" class="ml-2">mdi-form-select</v-icon>
                <span class="text-subtitle-1 font-weight-bold" :style="{ color: navy }">פרמטרים</span>
                <v-spacer></v-spacer>
                <v-btn small text color="grey darken-1" @click="resetForm">
                  <v-icon small left>mdi-restore</v-icon>
                  איפוס
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text class="pa-5">
                <v-row dense>
                  <v-col v-for="param in visibleParams" :key="param.name" cols="12" :sm="colSizeSm(param)"
                    :md="colSize(param)">
                    <div v-if="param.isDivider" class="advanced-divider">
                      <v-icon x-small :color="navy" class="ml-1">mdi-tune</v-icon>
                      <span>אפשרויות מתקדמות</span>
                    </div>

                    <div v-else class="param-block" :class="{ 'is-required': param.required }">
                      <div class="param-head">
                        <span class="param-label">{{ param.label }}</span>
                        <span v-if="param.required" class="required-dot">חובה</span>
                        <v-spacer></v-spacer>
                        <span class="param-name" dir="ltr">{{ param.name }}</span>
                        <span class="param-in" :class="param.in">{{ param.in === 'query' ? 'URL' : 'BODY' }}</span>
                      </div>

                      <!-- שם טבלה: כולל טעינת שדות להשלמה אוטומטית -->
                      <div v-if="param.isTableName">
                        <v-text-field v-model="form[param.name]" outlined dense hide-details="auto" dir="ltr"
                          name="pg-table-ref" autocomplete="off" spellcheck="false" :color="navy"
                          :placeholder="param.placeholder || 'table32922'" :hint="param.hint"
                          :persistent-hint="!!param.hint" :error-messages="fieldError(param)"
                          @change="rememberTableName(form[param.name])">
                          <template v-slot:append-outer>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small v-on="on" v-bind="attrs" :loading="fieldsLoading"
                                  @click="loadTableFields(form[param.name])">
                                  <v-icon :color="navy">mdi-table-search</v-icon>
                                </v-btn>
                              </template>
                              <span>טעינת שדות הטבלה להשלמה אוטומטית</span>
                            </v-tooltip>
                          </template>
                        </v-text-field>

                        <div v-if="tableFields.length && loadedTableName === form[param.name]" class="fields-loaded">
                          <v-icon x-small color="green darken-2" class="ml-1">mdi-check-circle-outline</v-icon>
                          <span>נטענו {{ tableFields.length }} שדות</span>
                          <v-btn x-small text :color="navy" class="mr-1" @click="fieldsDialog = true">הצגה</v-btn>
                        </div>
                      </div>

                      <!-- טקסט / מספר -->
                      <v-text-field v-else-if="param.type === 'string' || param.type === 'number'"
                        v-model="form[param.name]" :type="param.type === 'number' ? 'number' : 'text'" outlined dense
                        hide-details="auto" :color="navy" :name="'pg-' + param.name" autocomplete="off"
                        :placeholder="param.placeholder || ''" :hint="param.hint" :persistent-hint="!!param.hint"
                        :dir="param.type === 'number' ? 'ltr' : 'auto'"
                        :error-messages="fieldError(param)"></v-text-field>

                      <!-- בוליאני -->
                      <v-switch v-else-if="param.type === 'boolean'" v-model="form[param.name]" :color="navy" inset
                        dense hide-details="auto" class="mt-1" :label="form[param.name] ? 'כן' : 'לא'"
                        :hint="param.hint" :persistent-hint="!!param.hint"></v-switch>

                      <!-- בחירה -->
                      <v-select v-else-if="param.type === 'select'" v-model="form[param.name]" :items="param.options"
                        outlined dense hide-details="auto" :color="navy" :hint="param.hint"
                        :persistent-hint="!!param.hint" :error-messages="fieldError(param)"></v-select>

                      <!-- מערך מחרוזות -->
                      <v-combobox v-else-if="param.type === 'stringArray'" v-model="form[param.name]"
                        :items="fieldNames" multiple chips deletable-chips small-chips outlined dense
                        hide-details="auto" :color="navy" :hint="param.hint || 'הקלידו ולחצו Enter'"
                        persistent-hint></v-combobox>

                      <!-- זוגות שדה/ערך -->
                      <div v-else-if="param.type === 'keyValue'">
                        <div v-for="(pair, idx) in form[param.name]" :key="idx" class="builder-row">
                          <v-combobox v-model="pair.key" :items="fieldNames" label="שם השדה" outlined dense hide-details
                            autocomplete="off" :color="navy" class="ml-2"></v-combobox>
                          <v-text-field v-model="pair.value" label="ערך" outlined dense hide-details autocomplete="off"
                            :color="navy" class="ml-2"></v-text-field>
                          <v-btn icon small color="red darken-1" :disabled="form[param.name].length === 1"
                            @click="form[param.name].splice(idx, 1)">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                        <v-btn small text :color="navy" class="mt-1"
                          @click="form[param.name].push({ key: '', value: '' })">
                          <v-icon small left>mdi-plus</v-icon>
                          הוספת שדה
                        </v-btn>
                        <div v-if="param.hint" class="param-hint">{{ param.hint }}</div>
                      </div>

                      <!-- בונה תנאים -->
                      <div v-else-if="param.type === 'conditions'">
                        <div v-for="(item, idx) in form[param.name]" :key="idx">
                          <!-- קבוצת תנאים -->
                          <div v-if="isGroup(item)" class="condition-group">
                            <div class="d-flex align-center mb-2">
                              <v-icon x-small :color="navy" class="ml-1">mdi-code-brackets</v-icon>
                              <span class="caption font-weight-medium" :style="{ color: navy }">קבוצת תנאים</span>
                              <v-select v-model="item.logic" :items="logicOptions" dense hide-details solo flat
                                background-color="transparent" class="logic-select mr-2"></v-select>
                              <v-spacer></v-spacer>
                              <v-btn icon x-small color="red darken-1" @click="form[param.name].splice(idx, 1)">
                                <v-icon small>mdi-close</v-icon>
                              </v-btn>
                            </div>
                            <div v-for="(sub, subIdx) in item.group" :key="subIdx" class="builder-row">
                              <v-combobox v-model="sub.key" :items="fieldNames" label="שדה" outlined dense hide-details
                                autocomplete="off" :color="navy" class="ml-2"></v-combobox>
                              <v-select v-model="sub.operator" :items="operatorOptions" label="תנאי" outlined dense
                                hide-details :color="navy" class="ml-2 op-select"></v-select>
                              <v-text-field v-model="sub.value" label="ערך" outlined dense hide-details :color="navy"
                                class="ml-2"></v-text-field>
                              <v-btn icon small color="red darken-1" :disabled="item.group.length === 1"
                                @click="item.group.splice(subIdx, 1)">
                                <v-icon small>mdi-close</v-icon>
                              </v-btn>
                            </div>
                            <v-btn x-small text :color="navy"
                              @click="item.group.push({ key: '', operator: '==', value: '' })">
                              <v-icon x-small left>mdi-plus</v-icon>
                              תנאי בקבוצה
                            </v-btn>
                          </div>

                          <!-- תנאי בודד -->
                          <div v-else class="builder-row">
                            <v-combobox v-model="item.key" :items="fieldNames" label="שדה" outlined dense hide-details
                              autocomplete="off" :color="navy" class="ml-2"></v-combobox>
                            <v-select v-model="item.operator" :items="operatorOptions" label="תנאי" outlined dense
                              hide-details :color="navy" class="ml-2 op-select"></v-select>
                            <v-text-field v-model="item.value" label="ערך" outlined dense hide-details :color="navy"
                              class="ml-2"></v-text-field>
                            <v-btn icon small color="red darken-1" :disabled="form[param.name].length === 1"
                              @click="form[param.name].splice(idx, 1)">
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>
                          </div>
                        </div>

                        <div class="mt-1">
                          <v-btn small text :color="navy"
                            @click="form[param.name].push({ key: '', operator: '==', value: '' })">
                            <v-icon small left>mdi-plus</v-icon>
                            תנאי
                          </v-btn>
                          <v-btn small text :color="navy"
                            @click="form[param.name].push({ logic: 'or', group: [{ key: '', operator: '==', value: '' }] })">
                            <v-icon small left>mdi-plus-box-multiple-outline</v-icon>
                            קבוצת תנאים
                          </v-btn>
                        </div>
                        <div v-if="param.hint" class="param-hint">{{ param.hint }}</div>
                      </div>

                      <!-- בונה עדכון שדות -->
                      <div v-else-if="param.type === 'fieldsToUpdate'">
                        <div v-for="(item, idx) in form[param.name]" :key="idx" class="builder-row">
                          <v-combobox v-model="item.key" :items="fieldNames" label="שדה" outlined dense hide-details
                            :color="navy" class="ml-2"></v-combobox>
                          <v-select v-model="item.update_type" :items="updateTypeOptions" label="סוג עדכון" outlined
                            dense hide-details :color="navy" class="ml-2 type-select"></v-select>
                          <v-text-field v-model="item.value" label="ערך" outlined dense hide-details :color="navy"
                            class="ml-2"></v-text-field>
                          <v-btn icon small color="red darken-1" :disabled="form[param.name].length === 1"
                            @click="form[param.name].splice(idx, 1)">
                            <v-icon small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                        <v-btn small text :color="navy" class="mt-1"
                          @click="form[param.name].push({ key: '', value: '', update_type: 'set' })">
                          <v-icon small left>mdi-plus</v-icon>
                          שדה לעדכון
                        </v-btn>
                      </div>

                      <!-- JSON חופשי -->
                      <div v-else-if="param.type === 'json'">
                        <v-textarea v-model="form[param.name]" outlined dense hide-details="auto" rows="5" dir="ltr"
                          class="json-input" :color="navy" spellcheck="false" autocomplete="off"
                          :error-messages="jsonError(param)"></v-textarea>
                        <div class="d-flex align-center mt-1">
                          <v-btn x-small text :color="navy" @click="formatJson(param)">
                            <v-icon x-small left>mdi-code-braces</v-icon>
                            סידור
                          </v-btn>
                          <v-btn v-if="param.template !== undefined" x-small text :color="navy"
                            @click="resetJson(param)">
                            <v-icon x-small left>mdi-restore</v-icon>
                            תבנית
                          </v-btn>
                          <span v-if="param.hint" class="param-hint mr-2 mb-0">{{ param.hint }}</span>
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" v-if="!endpoint.params.length">
                    <div class="grey--text caption">לפעולה זו אין פרמטרים – נדרש רק הטוקן.</div>
                  </v-col>
                </v-row>

                <div v-if="advancedParams.length" class="text-center mt-3">
                  <v-btn small text :color="navy" @click="showAdvanced = !showAdvanced">
                    <v-icon small left>{{ showAdvanced ? 'mdi-chevron-up' : 'mdi-tune' }}</v-icon>
                    {{ showAdvanced ? 'הסתרת האפשרויות המתקדמות' : 'אפשרויות מתקדמות (' + advancedParams.length + ')' }}
                  </v-btn>
                </div>
              </v-card-text>

              <v-divider></v-divider>

              <v-card-actions class="pa-4 action-bar">
                <v-btn :color="navy" dark large :loading="sending" :disabled="!canSend" @click="send" class="send-btn">
                  <v-icon left>mdi-send</v-icon>
                  שליחת הבקשה
                </v-btn>

                <v-menu offset-y top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn outlined large :color="navy" v-on="on" v-bind="attrs" class="mr-3">
                      <v-icon left>mdi-content-copy</v-icon>
                      העתקת קוד
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item v-for="lang in codeLangs" :key="lang.id" @click="copyCode(lang.id)">
                      <v-list-item-icon class="ml-2">
                        <v-icon small :color="navy">{{ lang.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>{{ lang.title }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-spacer></v-spacer>

                <span v-if="!token" class="caption red--text text--darken-2">
                  <v-icon small color="red darken-2" class="ml-1">mdi-alert-circle-outline</v-icon>
                  יש להזין טוקן כדי לשלוח בקשה
                </span>
                <span v-else-if="missingRequired.length" class="caption orange--text text--darken-3">
                  <v-icon small color="orange darken-3" class="ml-1">mdi-alert-outline</v-icon>
                  חסרים שדות חובה: {{ missingRequired.join(', ') }}
                </span>
              </v-card-actions>
            </v-card>

            <!-- תוצאה -->
            <v-card elevation="3" class="result-card">
              <v-tabs v-model="resultTab" :color="navy" slider-color="#ffb91a" show-arrows>
                <v-tab>
                  <v-icon small class="ml-2">mdi-code-json</v-icon>
                  תשובת השרת
                </v-tab>
                <v-tab>
                  <v-icon small class="ml-2">mdi-format-list-bulleted</v-icon>
                  כותרות
                </v-tab>
                <v-tab>
                  <v-icon small class="ml-2">mdi-xml</v-icon>
                  קוד לשליחה
                </v-tab>
                <v-tab>
                  <v-icon small class="ml-2">mdi-history</v-icon>
                  היסטוריה
                  <v-chip v-if="history.length" x-small class="mr-2">{{ history.length }}</v-chip>
                </v-tab>
              </v-tabs>

              <v-divider></v-divider>

              <v-tabs-items v-model="resultTab">
                <!-- תשובה -->
                <v-tab-item>
                  <div v-if="response" class="pa-4">
                    <div class="d-flex align-center flex-wrap mb-3">
                      <v-chip label dark :color="httpColor" class="ml-2">
                        <v-icon x-small left>{{ response.ok ? 'mdi-check' : 'mdi-close' }}</v-icon>
                        {{ response.status }} {{ response.statusText }}
                      </v-chip>
                      <v-chip label outlined small class="ml-2">
                        <v-icon x-small left>mdi-timer-outline</v-icon>
                        {{ response.timeMs }} מ"ש
                      </v-chip>
                      <v-chip label outlined small class="ml-2">
                        <v-icon x-small left>mdi-weight</v-icon>
                        {{ response.size }}
                      </v-chip>
                      <v-chip v-if="logicalStatus" label small dark :color="logicalStatus.color" class="ml-2">
                        status {{ logicalStatus.code }} · {{ logicalStatus.text }}
                      </v-chip>
                      <v-spacer></v-spacer>
                      <v-btn small text :color="navy" @click="copyText(response.body, 'התשובה הועתקה')">
                        <v-icon small left>mdi-content-copy</v-icon>
                        העתקה
                      </v-btn>
                    </div>

                    <pre class="json-view" dir="ltr" v-html="highlightedResponse"></pre>
                  </div>

                  <div v-else-if="networkError" class="pa-6">
                    <v-alert type="error" text prominent class="mb-0">
                      <div class="font-weight-bold mb-1">הבקשה לא הגיעה לשרת</div>
                      <div class="body-2">{{ networkError }}</div>
                      <div class="caption mt-2">
                        בדקו את כתובת השרת ואת נתיב הבסיס של הקבוצה. אם הכתובת נכונה, ייתכן שהדפדפן חסם את הבקשה
                        בגלל CORS – במקרה כזה יש לאשר את דומיין הפאנל בהגדרות ה-CORS של השרת.
                      </div>
                    </v-alert>
                  </div>

                  <div v-else class="empty-state">
                    <v-icon size="48" color="grey lighten-1">mdi-send-outline</v-icon>
                    <div class="mt-3 grey--text">מלאו את הפרמטרים ולחצו על "שליחת הבקשה" כדי לראות את התשובה כאן</div>
                  </div>
                </v-tab-item>

                <!-- כותרות -->
                <v-tab-item>
                  <div v-if="response" class="pa-4">
                    <div class="headers-title">כותרות שנשלחו</div>
                    <div v-for="(val, key) in response.requestHeaders" :key="'req-' + key" class="header-row">
                      <span class="header-key" dir="ltr">{{ key }}</span>
                      <span class="header-val" dir="ltr">{{ val }}</span>
                    </div>

                    <div class="headers-title mt-4">כותרות שהתקבלו</div>
                    <div v-for="(val, key) in response.headers" :key="'res-' + key" class="header-row">
                      <span class="header-key" dir="ltr">{{ key }}</span>
                      <span class="header-val" dir="ltr">{{ val }}</span>
                    </div>
                  </div>
                  <div v-else class="empty-state">
                    <v-icon size="48" color="grey lighten-1">mdi-format-list-bulleted</v-icon>
                    <div class="mt-3 grey--text">הכותרות יופיעו לאחר שליחת בקשה</div>
                  </div>
                </v-tab-item>

                <!-- קוד -->
                <v-tab-item>
                  <div class="pa-4">
                    <v-chip-group v-model="codeLangIndex" mandatory :active-class="'code-chip-active'">
                      <v-chip v-for="lang in codeLangs" :key="lang.id" small label outlined>
                        <v-icon x-small left>{{ lang.icon }}</v-icon>
                        {{ lang.title }}
                      </v-chip>
                    </v-chip-group>

                    <div class="d-flex justify-end">
                      <v-btn small text :color="navy" @click="copyCode(currentLang.id)">
                        <v-icon small left>mdi-content-copy</v-icon>
                        העתקה
                      </v-btn>
                    </div>

                    <pre class="code-view" dir="ltr">{{ generatedCode }}</pre>
                  </div>
                </v-tab-item>

                <!-- היסטוריה -->
                <v-tab-item>
                  <div v-if="history.length" class="pa-2">
                    <v-list dense>
                      <v-list-item v-for="(h, i) in history" :key="i" @click="restoreFromHistory(h)">
                        <v-list-item-icon class="ml-2">
                          <v-chip x-small label dark :color="h.ok ? 'green darken-2' : 'red darken-2'">
                            {{ h.status }}
                          </v-chip>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="body-2">{{ h.title }}</v-list-item-title>
                          <v-list-item-subtitle class="caption" dir="ltr">{{ h.url }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action class="caption grey--text">{{ h.time }}</v-list-item-action>
                      </v-list-item>
                    </v-list>
                    <div class="text-center pb-2">
                      <v-btn x-small text color="grey darken-1" @click="history = []">ניקוי ההיסטוריה</v-btn>
                    </div>
                  </div>
                  <div v-else class="empty-state">
                    <v-icon size="48" color="grey lighten-1">mdi-history</v-icon>
                    <div class="mt-3 grey--text">כאן יופיעו הבקשות ששלחתם בפעילות הנוכחית</div>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </template>

          <v-card v-else elevation="3">
            <div class="empty-state">
              <v-icon size="48" color="grey lighten-1">mdi-gesture-tap-button</v-icon>
              <div class="mt-3 grey--text">בחרו פעולה מהרשימה כדי להתחיל</div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- דיאלוג שדות הטבלה -->
    <v-dialog v-model="fieldsDialog" max-width="640" scrollable>
      <v-card>
        <v-card-title :style="{ color: navy }" class="text-subtitle-1 font-weight-bold">
          <v-icon :color="navy" class="ml-2">mdi-table-column</v-icon>
          שדות הטבלה {{ loadedTableName }}
          <v-spacer></v-spacer>
          <v-btn icon @click="fieldsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4" style="max-height: 60vh;">
          <div v-for="(f, i) in tableFields" :key="i" class="field-row" @click="copyText(f.name, 'שם השדה הועתק')">
            <span class="field-name">{{ f.name }}</span>
            <v-chip x-small label outlined class="ml-2">{{ f.type || 'טקסט' }}</v-chip>
            <v-spacer></v-spacer>
            <span class="field-id" dir="ltr">{{ f.id }}</span>
            <v-icon x-small class="mr-2">mdi-content-copy</v-icon>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <snack-bar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></snack-bar>
  </div>
</template>

<script>
import NavBar from '@/components/widgets/navBar.vue'
import SnackBar from '@/components/widgets/snackBar.vue'
import Auth from '@/services/auth.service.js'
import {
  BASE_URL,
  WORKSPACE_KEY,
  API_GROUPS,
  UTILITY_SECTIONS,
  ENDPOINTS,
  OPERATOR_OPTIONS,
  UPDATE_TYPE_OPTIONS,
  METHOD_COLORS,
  STATUS_LEGEND,
  findEndpoint,
  endpointUrl,
  buildDefaultValues,
} from '@/data/apiPlaygroundCatalog.js'

const SETTINGS_KEY = 'apiPlaygroundSettings'

export default {
  name: 'ApiPlaygroundView',
  components: { NavBar, SnackBar },

  data() {
    return {
      navy: '#0d2c6d',

      /* חיבור – הראוטינג קבוע בקטלוג, המשתמש משנה רק את אלה */
      baseUrl: BASE_URL,
      token: '',
      showToken: false,
      tokenLoading: false,
      workspaceId: '',
      workspaceKey: WORKSPACE_KEY,

      /* טבלה */
      lastTableName: '',
      loadedTableName: '',
      tableFields: [],
      fieldsLoading: false,
      fieldsDialog: false,

      /* קטלוג */
      groups: API_GROUPS,
      utilitySections: UTILITY_SECTIONS,
      endpoints: ENDPOINTS,
      operatorOptions: OPERATOR_OPTIONS,
      updateTypeOptions: UPDATE_TYPE_OPTIONS,
      statusLegend: STATUS_LEGEND,
      logicOptions: [
        { text: 'וגם', value: 'and' },
        { text: 'או', value: 'or' },
      ],

      /* מצב */
      search: '',
      selectedId: 'get_rows',
      form: {},
      showAdvanced: false,
      showLegend: false,
      sending: false,
      response: null,
      networkError: '',
      resultTab: 0,
      codeLangIndex: 0,
      history: [],
      showValidation: false,

      codeLangs: [
        { id: 'curl', title: 'cURL', icon: 'mdi-console' },
        { id: 'fetch', title: 'JavaScript fetch', icon: 'mdi-language-javascript' },
        { id: 'axios', title: 'axios', icon: 'mdi-nodejs' },
        { id: 'python', title: 'Python', icon: 'mdi-language-python' },
      ],

      snackbar: false,
      snackbarColorBt: 'green',
      snacbarText: '',
    }
  },

  computed: {
    endpoint() {
      return findEndpoint(this.selectedId)
    },

    currentLang() {
      return this.codeLangs[this.codeLangIndex] || this.codeLangs[0]
    },

    fieldNames() {
      return this.tableFields.map(f => f.name).filter(Boolean)
    },

    /* עץ הפעולות לסרגל הצד, מסונן לפי החיפוש */
    filteredTree() {
      const term = (this.search || '').trim().toLowerCase()
      const match = ep =>
        !term ||
        ep.title.toLowerCase().includes(term) ||
        ep.path.toLowerCase().includes(term) ||
        ep.id.toLowerCase().includes(term) ||
        (ep.description || '').toLowerCase().includes(term)

      const tree = []
      this.groups.forEach(group => {
        const groupEndpoints = this.endpoints.filter(e => e.group === group.id && match(e))
        if (!groupEndpoints.length) return

        let sections = []
        if (group.id === 'utility') {
          this.utilitySections.forEach(sec => {
            const list = groupEndpoints.filter(e => e.section === sec.id)
            if (list.length) sections.push({ id: sec.id, title: sec.title, endpoints: list })
          })
        } else {
          sections = [{ id: 'all', title: '', endpoints: groupEndpoints }]
        }

        tree.push({
          id: group.id,
          title: group.title,
          icon: group.icon,
          count: groupEndpoints.length,
          sections,
        })
      })
      return tree
    },

    /* ---------- פרמטרים: בסיסיים מול מתקדמים ---------- */
    basicParams() {
      return this.endpoint ? this.endpoint.params.filter(p => !p.advanced) : []
    },

    advancedParams() {
      return this.endpoint ? this.endpoint.params.filter(p => p.advanced) : []
    },

    visibleParams() {
      const list = this.basicParams.slice()
      if (this.showAdvanced && this.advancedParams.length) {
        list.push({ name: '__advanced__', isDivider: true })
        this.advancedParams.forEach(p => list.push(p))
      }
      return list
    },

    /* ---------- בניית הבקשה ---------- */
    queryParams() {
      if (!this.endpoint) return {}
      const out = {}

      // ה-workspaceId נכנס ראשון, בדיוק כמו באוסף הפוסטמן
      if (this.workspaceId && this.endpoint.workspaceIn === 'query') {
        out[this.workspaceKey] = this.workspaceId
      }

      this.endpoint.params
        .filter(p => p.in === 'query')
        .forEach(p => {
          const val = this.valueOf(p)
          if (val !== undefined) out[p.name] = val
        })

      return out
    },

    bodyPayload() {
      if (!this.endpoint) return null
      const out = {}

      if (this.workspaceId && this.endpoint.workspaceIn === 'body') {
        out[this.workspaceKey] = this.workspaceId
      }

      this.endpoint.params
        .filter(p => p.in === 'body')
        .forEach(p => {
          const val = this.valueOf(p)
          if (val !== undefined) out[p.name] = val
        })

      return Object.keys(out).length ? out : null
    },

    requestHeaders() {
      const headers = { Authorization: 'Bearer ' + (this.token || '<TOKEN>') }
      if (this.bodyPayload) headers['Content-Type'] = 'application/json'
      return headers
    },

    fullUrl() {
      return endpointUrl(this.endpoint)
    },

    previewUrl() {
      const qs = this.buildQueryString(this.queryParams)
      return this.fullUrl + qs
    },

    missingRequired() {
      if (!this.endpoint) return []
      return this.endpoint.params
        .filter(p => p.required && this.valueOf(p) === undefined)
        .map(p => p.label)
    },

    canSend() {
      return !!this.token && !!this.endpoint && !this.missingRequired.length && !this.hasJsonErrors
    },

    hasJsonErrors() {
      if (!this.endpoint) return false
      return this.endpoint.params.some(p => p.type === 'json' && this.jsonError(p).length > 0)
    },

    httpColor() {
      if (!this.response) return 'grey'
      if (this.response.status >= 500) return 'red darken-3'
      if (this.response.status >= 400) return 'orange darken-3'
      if (this.response.ok) return 'green darken-2'
      return 'grey darken-1'
    },

    logicalStatus() {
      if (!this.response || !this.response.json) return null
      const code = this.response.json.status
      if (code === undefined || code === null) return null
      const found = STATUS_LEGEND.find(s => String(s.code) === String(code))
      return found || { code, text: '', color: 'grey' }
    },

    highlightedResponse() {
      if (!this.response) return ''
      return this.highlightJson(this.response.body)
    },

    generatedCode() {
      return this.buildCode(this.currentLang.id)
    },
  },

  watch: {
    selectedId() {
      // נבנה מחדש לפני הרינדור כדי שהטופס תמיד יתאים לפעולה שנבחרה
      this.buildForm()
      this.showAdvanced = false
      this.response = null
      this.networkError = ''
      this.showValidation = false
      this.resultTab = 0
    },
    workspaceId() { this.saveSettings() },
    lastTableName() { this.saveSettings() },
  },

  methods: {
    /* ---------------------- ניווט וטופס ---------------------- */
    selectEndpoint(id) {
      this.selectedId = id
      this.buildForm()
    },

    buildForm() {
      const values = buildDefaultValues(this.endpoint)
      // מילוי אוטומטי של שם הטבלה מברירת המחדל
      if (this.lastTableName && this.endpoint) {
        this.endpoint.params.forEach(p => {
          if (p.isTableName && !values[p.name]) values[p.name] = this.lastTableName
        })
      }
      this.form = values
    },

    resetForm() {
      this.buildForm()
      this.showValidation = false
      this.showSnackBar('הטופס אופס', 'green')
    },

    /** שם הטבלה האחרון שהוזן נשמר, וממלא מראש את השדה בפעולה הבאה */
    rememberTableName(name) {
      if (name) this.lastTableName = name
    },

    colSize(param) {
      if (param.isDivider) return 12
      const wide = ['conditions', 'fieldsToUpdate', 'json', 'keyValue', 'stringArray']
      return wide.indexOf(param.type) !== -1 ? 12 : 4
    },

    /** רוחב בטאבלט: שדה רגיל חצי שורה, שדה מורכב שורה שלמה */
    colSizeSm(param) {
      return this.colSize(param) === 12 ? 12 : 6
    },

    isGroup(item) {
      return item && Object.prototype.hasOwnProperty.call(item, 'group')
    },

    permissionLabel(p) {
      const map = { write: 'כתיבה', delete: 'מחיקה', fields: 'עריכת שדות' }
      return map[p] || p
    },

    methodColor(method) {
      return METHOD_COLORS[method] || '#546e7a'
    },

    fieldError(param) {
      if (!this.showValidation) return []
      if (param.required && this.valueOf(param) === undefined) return ['שדה חובה']
      return []
    },

    jsonError(param) {
      const raw = this.form[param.name]
      if (!raw || !String(raw).trim()) return []
      try {
        JSON.parse(raw)
        return []
      } catch (e) {
        return ['JSON לא תקין']
      }
    },

    formatJson(param) {
      try {
        const parsed = JSON.parse(this.form[param.name])
        this.$set(this.form, param.name, JSON.stringify(parsed, null, 2))
      } catch (e) {
        this.showSnackBar('לא ניתן לסדר – ה-JSON אינו תקין', 'red')
      }
    },

    resetJson(param) {
      this.$set(this.form, param.name, JSON.stringify(param.template, null, 2))
    },

    /* ---------------------- בניית ערכים ---------------------- */
    /** מחזיר את הערך שיישלח לשרת, או undefined אם השדה ריק ואינו נדרש */
    valueOf(param) {
      const raw = this.form[param.name]

      switch (param.type) {
        case 'boolean':
          if (raw === true) return true
          return param.required ? !!raw : undefined

        case 'number': {
          if (raw === '' || raw === null || raw === undefined) return undefined
          const n = Number(raw)
          return isNaN(n) ? undefined : n
        }

        case 'conditions': {
          const cleaned = (raw || [])
            .map(item => {
              if (this.isGroup(item)) {
                const inner = (item.group || []).filter(c => c.key !== '' && c.key !== null && c.key !== undefined)
                if (!inner.length) return null
                return { logic: item.logic || 'and', group: inner }
              }
              return item.key ? item : null
            })
            .filter(Boolean)
          return cleaned.length ? cleaned : undefined
        }

        case 'fieldsToUpdate': {
          const cleaned = (raw || [])
            .filter(f => f.key)
            .map(f => {
              const out = { key: f.key, value: f.value }
              if (f.update_type && f.update_type !== 'set') out.update_type = f.update_type
              return out
            })
          return cleaned.length ? cleaned : undefined
        }

        case 'keyValue': {
          const obj = {}
            ; (raw || []).forEach(pair => {
              if (pair.key) obj[pair.key] = pair.value
            })
          return Object.keys(obj).length ? obj : undefined
        }

        case 'stringArray':
          return raw && raw.length ? raw : undefined

        case 'json': {
          if (!raw || !String(raw).trim()) return undefined
          try {
            return JSON.parse(raw)
          } catch (e) {
            return undefined
          }
        }

        default:
          if (raw === '' || raw === null || raw === undefined) return undefined
          return raw
      }
    },

    buildQueryString(params) {
      const keys = Object.keys(params)
      if (!keys.length) return ''
      const parts = keys.map(k => {
        const v = params[k]
        const str = typeof v === 'object' ? JSON.stringify(v) : String(v)
        return encodeURIComponent(k) + '=' + encodeURIComponent(str)
      })
      return '?' + parts.join('&')
    },

    /* ---------------------- שליחה ---------------------- */
    async send() {
      this.showValidation = true
      if (!this.canSend) {
        this.showSnackBar('יש להשלים את שדות החובה לפני השליחה', 'red')
        return
      }

      this.sending = true
      this.response = null
      this.networkError = ''
      const started = performance.now()

      try {
        const options = {
          method: this.endpoint.method,
          headers: Object.assign({}, this.requestHeaders),
        }
        if (this.bodyPayload) options.body = JSON.stringify(this.bodyPayload)

        const res = await fetch(this.previewUrl, options)
        const text = await res.text()
        const elapsed = Math.round(performance.now() - started)

        let parsed = null
        try {
          parsed = JSON.parse(text)
        } catch (e) {
          parsed = null
        }

        const headers = {}
        res.headers.forEach((value, key) => {
          headers[key] = value
        })

        const maskedHeaders = Object.assign({}, this.requestHeaders)
        if (maskedHeaders.Authorization) maskedHeaders.Authorization = 'Bearer ' + this.maskToken(this.token)

        this.response = {
          status: res.status,
          statusText: res.statusText,
          ok: res.ok,
          timeMs: elapsed,
          size: this.humanSize(text.length),
          body: parsed ? JSON.stringify(parsed, null, 2) : text,
          json: parsed,
          headers,
          requestHeaders: maskedHeaders,
        }

        this.pushHistory(res.status, res.ok)
        this.resultTab = 0
      } catch (error) {
        this.networkError = error && error.message ? error.message : String(error)
        this.resultTab = 0
      } finally {
        this.sending = false
      }
    },

    pushHistory(status, ok) {
      const now = new Date()
      this.history.unshift({
        endpointId: this.endpoint.id,
        title: this.endpoint.title,
        url: this.previewUrl,
        status,
        ok,
        time: now.toLocaleTimeString('he-IL'),
        form: JSON.parse(JSON.stringify(this.form)),
      })
      if (this.history.length > 25) this.history.pop()
    },

    restoreFromHistory(item) {
      this.selectedId = item.endpointId
      this.$nextTick(() => {
        this.form = JSON.parse(JSON.stringify(item.form))
        this.resultTab = 0
        this.showSnackBar('הבקשה נטענה מההיסטוריה', 'green')
      })
    },

    /* ---------------------- טעינת נתונים ---------------------- */
    /** silent = טעינה אוטומטית בכניסה לעמוד, בלי הודעות */
    async loadMyToken(silent) {
      this.tokenLoading = true
      try {
        const sessionToken = localStorage.getItem('token')
        const api = process.env.VUE_APP_BASE_URL + '/user/get_user_details'
        const res = await fetch(api, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + sessionToken },
        })
        const data = await res.json()
        if (res.status === 200 && data.api_token) {
          this.token = data.api_token
          if (!silent) this.showSnackBar('הטוקן נטען מהחשבון שלך', 'green')
        } else if (!silent) {
          this.showSnackBar('לא נמצא טוקן בחשבון. אפשר להעתיק אותו מהאזור האישי', 'red')
        }
      } catch (error) {
        if (!silent) this.showSnackBar('טעינת הטוקן נכשלה: ' + error, 'red')
      } finally {
        this.tokenLoading = false
      }
    },

    async loadTableFields(tableName) {
      if (!this.token) {
        this.showSnackBar('יש להזין טוקן לפני טעינת השדות', 'red')
        return
      }
      if (!tableName) {
        this.showSnackBar('יש להזין שם טבלה', 'red')
        return
      }

      this.fieldsLoading = true
      try {
        const fieldsEndpoint = findEndpoint('get_table_fields')
        const params = {}
        if (this.workspaceId) params[this.workspaceKey] = this.workspaceId
        params.tableName = tableName
        const url = endpointUrl(fieldsEndpoint) + this.buildQueryString(params)

        const res = await fetch(url, {
          method: 'GET',
          headers: { Authorization: 'Bearer ' + this.token },
        })
        const data = await res.json()

        if (res.status === 200 && Array.isArray(data.fields)) {
          this.tableFields = data.fields
          this.loadedTableName = tableName
          this.rememberTableName(tableName)
          this.showSnackBar('נטענו ' + data.fields.length + ' שדות מהטבלה', 'green')
        } else {
          this.tableFields = []
          this.loadedTableName = ''
          this.showSnackBar(data.message || 'לא הצלחנו לטעון את שדות הטבלה', 'red')
        }
      } catch (error) {
        this.tableFields = []
        this.loadedTableName = ''
        this.showSnackBar('טעינת השדות נכשלה. בדקו את שם הטבלה ואת הטוקן', 'red')
      } finally {
        this.fieldsLoading = false
      }
    },

    /* ---------------------- יצירת קוד ---------------------- */
    buildCode(lang) {
      if (!this.endpoint) return ''
      const url = this.previewUrl
      const method = this.endpoint.method
      const body = this.bodyPayload
      const headers = Object.assign({}, this.requestHeaders)

      if (lang === 'curl') {
        const lines = ["curl -X " + method + " '" + url + "' \\"]
        Object.keys(headers).forEach(k => {
          lines.push("  -H '" + k + ": " + headers[k] + "' \\")
        })
        if (body) {
          lines.push("  --data-raw '" + JSON.stringify(body) + "'")
        } else {
          lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, '')
        }
        return lines.join('\n')
      }

      if (lang === 'fetch') {
        return (
          "const response = await fetch('" + url + "', {\n" +
          "  method: '" + method + "',\n" +
          '  headers: ' + JSON.stringify(headers, null, 2).replace(/\n/g, '\n  ') + ',\n' +
          (body ? '  body: JSON.stringify(' + JSON.stringify(body, null, 2).replace(/\n/g, '\n  ') + '),\n' : '') +
          '});\n\n' +
          'const data = await response.json();\n' +
          'console.log(data);'
        )
      }

      if (lang === 'axios') {
        const config =
          '{\n' +
          "  method: '" + method.toLowerCase() + "',\n" +
          "  url: '" + url + "',\n" +
          '  headers: ' + JSON.stringify(headers, null, 2).replace(/\n/g, '\n  ') +
          (body ? ',\n  data: ' + JSON.stringify(body, null, 2).replace(/\n/g, '\n  ') : '') +
          '\n}'
        return "import axios from 'axios';\n\nconst { data } = await axios(" + config + ');\nconsole.log(data);'
      }

      if (lang === 'python') {
        return (
          'import requests\n\n' +
          "url = '" + url + "'\n" +
          'headers = ' + this.toPythonDict(headers) + '\n' +
          (body ? 'payload = ' + this.toPythonDict(body) + '\n' : '') +
          '\nresponse = requests.' + method.toLowerCase() + '(url, headers=headers' +
          (body ? ', json=payload' : '') + ')\n' +
          'print(response.json())'
        )
      }

      return ''
    },

    toPythonDict(obj) {
      return JSON.stringify(obj, null, 4)
        .replace(/: true/g, ': True')
        .replace(/: false/g, ': False')
        .replace(/: null/g, ': None')
    },

    copyCode(lang) {
      this.copyText(this.buildCode(lang), 'הקוד הועתק')
    },

    /* ---------------------- עזרים ---------------------- */
    copyText(text, message) {
      if (!text) return
      navigator.clipboard.writeText(text).then(
        () => this.showSnackBar(message || 'הועתק', 'green'),
        () => this.showSnackBar('ההעתקה נכשלה', 'red')
      )
    },

    maskToken(token) {
      if (!token) return ''
      if (token.length <= 8) return '••••'
      return token.slice(0, 4) + '••••••••' + token.slice(-4)
    },

    humanSize(chars) {
      if (chars < 1024) return chars + ' תווים'
      return (chars / 1024).toFixed(1) + ' KB'
    },

    highlightJson(text) {
      if (!text) return ''
      const escaped = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      return escaped.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        match => {
          let cls = 'jv-number'
          if (/^"/.test(match)) {
            cls = /:$/.test(match) ? 'jv-key' : 'jv-string'
          } else if (/true|false/.test(match)) {
            cls = 'jv-bool'
          } else if (/null/.test(match)) {
            cls = 'jv-null'
          }
          return '<span class="' + cls + '">' + match + '</span>'
        }
      )
    },

    showSnackBar(message, color) {
      this.snacbarText = message
      this.snackbarColorBt = color
      this.snackbar = true
    },

    /* ---------------------- שמירת הגדרות ---------------------- */
    saveSettings() {
      try {
        const settings = {
          workspaceId: this.workspaceId,
          lastTableName: this.lastTableName,
        }
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
      } catch (e) {
        /* אחסון חסום – ממשיכים בלי שמירה */
      }
    },

    loadSettings() {
      try {
        const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}')
        this.workspaceId = saved.workspaceId || ''
        this.lastTableName = saved.lastTableName || ''
      } catch (e) {
        /* אין הגדרות שמורות */
      }
    },
  },

  mounted() {
    document.title = 'סביבת בדיקות API | CLICKCALL'

    const auth = Auth.checkAuth()
    if (!auth) {
      this.$router.replace({ name: 'login' })
      return
    }

    this.loadSettings()
    this.buildForm()
    this.loadMyToken(true)
  },
}
</script>

<style lang="scss" scoped>
$navy: #0d2c6d;
$yellow: #ffb91a;

.playground-page {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.playground-container {
  min-height: calc(100vh - 64px);
  max-width: 1580px;
  margin: 0 auto;
  padding-top: 76px;
  padding-bottom: 48px;
  padding-left: 28px;
  padding-right: 28px;
  background-color: transparent;
}

.masked-field {
  ::v-deep input {
    -webkit-text-security: disc;
    letter-spacing: 1px;
  }
}

.fields-loaded {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: #2e7d32;
  margin-top: 6px;
}

/* ---------- כותרת ---------- */
.hero-card {
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.hero-strip {
  height: 5px;
  background: linear-gradient(90deg, $yellow 0%, #ffd166 50%, $navy 100%);
}

.legend-box {
  background-color: #fafbfd;
  border: 1px solid #e8eaf0;
  border-radius: 8px;
  padding: 14px 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin: 4px 0;
  min-width: 260px;
  font-size: 13px;
}

/* ---------- חיבור ---------- */
.conn-card {
  border-radius: 10px;
  border-top: 3px solid $yellow;
}

.conn-footer {
  border-top: 1px dashed #e8eaf0;
  padding-top: 10px;
}

.advanced-divider {
  display: flex;
  align-items: center;
  color: #8794aa;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  margin: 6px 0 2px;

  &:after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #e8eaf0;
    margin-right: 10px;
  }
}

/* ---------- סרגל הפעולות ---------- */
.sidebar-card {
  border-radius: 10px;
  position: sticky;
  top: 76px;
  overflow: hidden;
}

.endpoint-list {
  max-height: calc(100vh - 260px);
  overflow-y: auto;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background-color: #f7f8fb;
  border-bottom: 1px solid #eceef4;
  font-size: 13px;
  font-weight: 700;
  color: $navy;
  position: sticky;
  top: 0;
  z-index: 1;
}

.group-count {
  background-color: rgba(13, 44, 109, 0.08);
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
}

.section-header {
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  color: #90a0b8;
  letter-spacing: 0.3px;
}

.endpoint-item {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  cursor: pointer;
  border-right: 3px solid transparent;
  transition: background-color 0.15s, border-color 0.15s;

  &:hover {
    background-color: #f5f7fb;
  }

  &.active {
    background-color: rgba(255, 185, 26, 0.14);
    border-right-color: $yellow;

    .endpoint-title {
      font-weight: 700;
      color: $navy;
    }
  }
}

.endpoint-title {
  font-size: 13.5px;
  color: #33415c;
  line-height: 1.3;
}

.method-tag {
  display: inline-block;
  min-width: 52px;
  text-align: center;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  border-radius: 4px;
  padding: 2px 6px;
  margin-left: 10px;
  font-family: 'Courier New', monospace;

  &.lg {
    min-width: 66px;
    font-size: 12px;
    padding: 4px 8px;
  }
}

/* ---------- פעולה נבחרת ---------- */
.endpoint-card {
  border-radius: 10px;
  border-right: 4px solid $navy;
}

.url-bar {
  display: flex;
  align-items: center;
  background-color: #f7f8fb;
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  padding: 8px 10px;
  overflow-x: auto;
}

.url-text {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #2b3a55;
  white-space: nowrap;
  flex-grow: 1;
  margin: 0 4px;
}

.note-line {
  display: flex;
  align-items: flex-start;
  font-size: 12.5px;
  color: #6b7a90;
  line-height: 1.6;
}

/* ---------- פרמטרים ---------- */
.param-block {
  background-color: #fcfcfe;
  border: 1px solid #eaecf3;
  border-radius: 8px;
  padding: 12px 14px;
  height: 100%;

  &.is-required {
    border-color: #dfe4ef;
    background-color: #fff;
  }
}

.param-head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.param-label {
  font-size: 13.5px;
  font-weight: 600;
  color: $navy;
}

.required-dot {
  font-size: 10px;
  color: #c62828;
  background-color: rgba(198, 40, 40, 0.08);
  border-radius: 4px;
  padding: 1px 5px;
  margin-right: 6px;
}

.param-name {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #8794aa;
  margin-left: 6px;
}

.param-in {
  font-size: 9.5px;
  font-weight: 700;
  border-radius: 3px;
  padding: 1px 5px;
  letter-spacing: 0.3px;

  &.query {
    color: #00695c;
    background-color: rgba(0, 105, 92, 0.1);
  }

  &.body {
    color: #4527a0;
    background-color: rgba(69, 39, 160, 0.1);
  }
}

.param-hint {
  font-size: 11.5px;
  color: #8794aa;
  margin-top: 6px;
}

.builder-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  .op-select {
    max-width: 150px;
  }

  .type-select {
    max-width: 190px;
  }
}

.condition-group {
  border: 1px dashed #c9d2e3;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  background-color: #f9fafd;
}

.logic-select {
  max-width: 90px;

  ::v-deep .v-input__slot {
    padding: 0 !important;
    min-height: 24px !important;
  }

  ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 700;
    color: #0d2c6d;
  }
}

.json-input {
  ::v-deep textarea {
    font-family: 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.6;
  }
}

/* ---------- פעולות ותוצאה ---------- */
.action-bar {
  background-color: #fbfcfe;
}

.send-btn {
  min-width: 170px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.result-card {
  border-radius: 10px;
  overflow: hidden;
}

.empty-state {
  text-align: center;
  padding: 56px 20px;
}

.json-view {
  background-color: #0f2038;
  color: #e6edf6;
  border-radius: 8px;
  padding: 16px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.7;
  max-height: 520px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;

  ::v-deep .jv-key {
    color: #ffd166;
  }

  ::v-deep .jv-string {
    color: #8fe3a1;
  }

  ::v-deep .jv-number {
    color: #7bc5ff;
  }

  ::v-deep .jv-bool {
    color: #ff9d7a;
  }

  ::v-deep .jv-null {
    color: #a0aec0;
  }
}

.code-view {
  background-color: #f7f8fb;
  border: 1px solid #e6e9f0;
  border-radius: 8px;
  padding: 16px;
  font-family: 'Courier New', monospace;
  font-size: 12.5px;
  line-height: 1.7;
  color: #2b3a55;
  max-height: 460px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin-top: 8px;
}

.code-chip-active {
  background-color: rgba(13, 44, 109, 0.08) !important;
  border-color: #0d2c6d !important;
  color: #0d2c6d !important;
}

.headers-title {
  font-size: 12px;
  font-weight: 700;
  color: $navy;
  margin-bottom: 6px;
}

.header-row {
  display: flex;
  padding: 5px 0;
  border-bottom: 1px solid #f0f2f7;
  font-size: 12.5px;
}

.header-key {
  font-family: 'Courier New', monospace;
  color: #6b7a90;
  min-width: 200px;
}

.header-val {
  font-family: 'Courier New', monospace;
  color: #2b3a55;
  word-break: break-all;
}

.field-row {
  display: flex;
  align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid #f0f2f7;
  cursor: pointer;

  &:hover {
    background-color: #f7f8fb;
  }
}

.field-name {
  font-weight: 600;
  font-size: 13.5px;
  color: #2b3a55;
}

.field-id {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #9aa6ba;
}

/* ---------- התאמות RTL ומובייל ---------- */
::v-deep .v-icon {
  margin-left: 0;
}

@media (max-width: 960px) {
  .playground-container {
    padding-top: 70px;
    padding-left: 12px;
    padding-right: 12px;
  }

  .sidebar-card {
    position: static;
  }

  .endpoint-list {
    max-height: 340px;
  }

  .builder-row {
    flex-wrap: wrap;

    .op-select,
    .type-select {
      max-width: 100%;
    }

    >* {
      margin-bottom: 6px;
    }
  }
}
</style>